import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import Loading from '../components/Loading';
import GlobalAppointmentsModal from '../components/appointments/GlobalAppointmentsModal';
import {
    APPOINTMENT_PATIENT_QUERY_PARAM,
    APPOINTMENT_QUERY_PARAM,
    NEW_APPOINTMENT_PARAM
} from '../components/appointments/AppointmentsModal';
import UploadRecordsModal from '../components/modals/UploadRecordsModal';
import UploadRefinementRecordsModal from '../components/modals/UploadRefinementRecordsModal';
import UploadRetentionRecordsModal from '../components/modals/UploadRetentionRecordsModal';
import StartMonitoringModal from '../components/modals/StartMonitoringModal';
import RespondToQuestionModal from '../components/modals/RespondToQuestionModal';
import { useRecentActions } from './RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { fetchPatientForMonitoring, StartMonitoringPatient } from '../services/patient';
import { startMonitoringWorkflow, StartMonitoringParams } from '../services/patient/delivery';
import { toApiError } from '../services/apiErrors';
import { APPOINTMENTS_QUERY_OPEN, APPOINTMENTS_QUERY_PARAM } from '../utils/constants';
import { useRefinementWorkflow } from '../hooks/useRefinementWorkflow';
import { useRetentionWorkflow } from '../hooks/useRetentionWorkflow';
import { useUploadRecordsWorkflow } from '../hooks/useUploadRecordsWorkflow';
import { useRespondToQuestionWorkflow } from '../hooks/useRespondToQuestionWorkflow';

interface PatientActionsContextType {
    handleAction: (action: string, patientId: string) => Promise<boolean>;
    openUploadRetention: (patientId: string, initialStep?: 'intro' | 'congratulations') => void;
}

const PatientActionsContext = createContext<PatientActionsContextType | undefined>(undefined);

export const usePatientActions = () => {
    const context = useContext(PatientActionsContext);
    if (!context) {
        throw new Error('usePatientActions must be used within PatientActionsProvider');
    }
    return context;
};

interface PatientActionsProviderProps {
    children: ReactNode;
}

export const PatientActionsProvider: React.FC<PatientActionsProviderProps> = ({ children }) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();
    const [, setSearchParams] = useSearchParams();
    const [isLoadingPatient, setIsLoadingPatient] = useState(false);

    // Monitoring
    const [isMonitoringModalOpen, setIsMonitoringModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState<StartMonitoringPatient | null>(null);
    const [isMonitoringSubmitting, setIsMonitoringSubmitting] = useState(false);

    // Retention workflow (upload-records / congratulations modal)
    const retention = useRetentionWorkflow();

    // Refinement workflow (upload-records modal)
    const refinement = useRefinementWorkflow();

    // Upload records workflow (records-incomplete hold -> exit hold)
    const uploadRecords = useUploadRecordsWorkflow();

    // Respond-to-question workflow (clinical-question hold -> exit hold)
    const respondToQuestion = useRespondToQuestionWorkflow();

    const handleStartMonitoring = useCallback(
        async (patientId: string) => {
            setIsLoadingPatient(true);
            try {
                const patientData = await fetchPatientForMonitoring(patientId);
                setSelectedPatient(patientData);
                setIsMonitoringModalOpen(true);
            } catch (error) {
                showToast('Failed to load patient monitoring data.', 'error');
            } finally {
                setIsLoadingPatient(false);
            }
        },
        [showToast]
    );

    // Opens the global AppointmentsModal (mounted below, see
    // GlobalAppointmentsModal) straight into a new-appointment composer
    // preselected for `patientId` - the same
    // `?appointments=open&appointment=new&appointmentPatient=<id>` deep-link
    // contract NextAppointmentCell's "+" button already uses. No navigation is
    // needed: the modal reads these params from wherever it's mounted.
    const openScheduleAppointment = useCallback(
        (patientId: string) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN);
                next.set(APPOINTMENT_QUERY_PARAM, NEW_APPOINTMENT_PARAM);
                next.set(APPOINTMENT_PATIENT_QUERY_PARAM, patientId);
                return next;
            });
        },
        [setSearchParams]
    );

    const handleAction = useCallback(
        async (action: string, patientId: string): Promise<boolean> => {
            switch (action) {
                case 'Start monitoring':
                    await handleStartMonitoring(patientId);
                    return true;
                case 'Schedule appointment':
                    openScheduleAppointment(patientId);
                    return true;
                case 'Upload refinement records':
                    refinement.openUploadRefinement(patientId);
                    return true;
                case 'Upload retention records':
                    retention.openUploadRetention(patientId);
                    return true;
                case 'Upload records':
                    await uploadRecords.open(patientId);
                    return true;
                case 'Respond to question':
                    await respondToQuestion.open(patientId);
                    return true;
                // 'Review for refinement' / 'Review for retention' navigate to their
                // routed pages (handled in ActionRequiredCell) — return false here.
                default:
                    return false;
            }
        },
        [
            handleStartMonitoring,
            openScheduleAppointment,
            refinement.openUploadRefinement,
            retention.openUploadRetention,
            uploadRecords.open,
            respondToQuestion.open
        ]
    );

    const handleMonitoringConfirm = async (params: StartMonitoringParams) => {
        if (!selectedPatient?.id) return;

        setIsMonitoringSubmitting(true);
        try {
            await startMonitoringWorkflow(selectedPatient.id, params);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({ queryKey: ['patient', selectedPatient.id] });
            markActionCompleted(selectedPatient.id, 'Start monitoring');
        } catch (error) {
            showToast(toApiError(error).message, 'error');
        } finally {
            setIsMonitoringSubmitting(false);
            setIsMonitoringModalOpen(false);
            setSelectedPatient(null);
        }
    };

    const value = useMemo(
        () => ({
            handleAction,
            openUploadRetention: retention.openUploadRetention
        }),
        [handleAction, retention.openUploadRetention]
    );

    return (
        <PatientActionsContext.Provider value={value}>
            {children}

            {/* Modals */}
            {isLoadingPatient && <Loading text="Loading patient..." />}
            <StartMonitoringModal
                isOpen={isMonitoringModalOpen}
                onClose={() => setIsMonitoringModalOpen(false)}
                onConfirm={handleMonitoringConfirm}
                patient={selectedPatient}
                isSubmitting={isMonitoringSubmitting}
            />
            <UploadRefinementRecordsModal
                isOpen={refinement.isUploadRefinementModalOpen}
                patientId={refinement.uploadRefinementPatientId}
                onClose={refinement.closeUploadRefinement}
                onComplete={refinement.handleUploadComplete}
                isSubmitting={refinement.isSubmitting}
            />
            <UploadRetentionRecordsModal
                isOpen={retention.isUploadRetentionModalOpen}
                patientId={retention.uploadRetentionPatientId}
                initialStep={retention.retentionInitialStep}
                onClose={retention.closeUploadRetention}
                onComplete={retention.handleComplete}
                isSubmitting={retention.isSubmitting}
            />
            <UploadRecordsModal
                isOpen={uploadRecords.isOpen}
                patientId={uploadRecords.patient?.id ?? null}
                patientName={uploadRecords.patient?.name ?? ''}
                statusLabel={uploadRecords.patient?.statusLabel ?? 'On Hold'}
                rejectionNote={uploadRecords.rejectionNote}
                rejectionEventId={uploadRecords.rejectionEventId}
                isLoadingNote={uploadRecords.isLoadingNote}
                isSubmitting={uploadRecords.isSubmitting}
                onClose={uploadRecords.close}
                onSubmit={uploadRecords.submit}
            />
            <RespondToQuestionModal
                isOpen={respondToQuestion.isOpen}
                patientId={respondToQuestion.patient?.id ?? null}
                patientName={respondToQuestion.patient?.name ?? ''}
                statusLabel={respondToQuestion.patient?.statusLabel ?? 'On Hold'}
                isSubmitting={respondToQuestion.isSubmitting}
                onClose={respondToQuestion.close}
                onSubmit={respondToQuestion.submit}
            />
            <GlobalAppointmentsModal />
        </PatientActionsContext.Provider>
    );
};
